<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-card-container anna-review">
    <p v-if="annaDown" class="anna-offline">{{ status.message }}</p>
    <div v-if="isLoading" class="anna-review-empty">Loading ANNA's review…</div>
    <div v-else-if="unavailable" class="anna-review-empty">
      ANNA's review couldn't be loaded right now. The candidate's details above
      aren't affected; try again later.
    </div>
    <div v-else-if="!review" class="anna-review-empty anna-review-start">
      <span>ANNA hasn't reviewed this candidate yet.</span>
      <oxd-button
        v-if="canAct"
        display-type="secondary"
        :label="scoring ? 'ANNA is reading the CV…' : 'Score CV with ANNA'"
        :disabled="scoring || annaDown"
        @click="scoreCv"
      />
    </div>
    <template v-else>
      <!-- Header: overall score, ANNA's suggested step, sub-scores -->
      <div class="anna-review-head">
        <div class="anna-review-score">
          <div
            :class="[
              'anna-overall',
              `anna-tone--${tone(review.scores.overall)}`,
            ]"
          >
            {{ review.scores.overall ?? '—' }}<span>/100</span>
            <div class="anna-overall-label">Overall</div>
          </div>
          <div class="anna-next">
            <div class="anna-review-label">
              Next step
              <span
                v-if="review.stage"
                :class="['anna-stage', `anna-tone--${stageTone}`]"
                :title="`Where the candidate is with Anna (the Candidates list shows the same). The status at the top is the application's own.`"
              >
                {{ review.stage.label }}
              </span>
            </div>
            <div class="anna-next-text">{{ nextStep }}</div>
          </div>
        </div>
        <div class="anna-subscores">
          <div v-for="s in subScores" :key="s.label" class="anna-subscore">
            <div
              :class="[
                'anna-subscore-val',
                `anna-tone--${tone(s.value, s.max)}`,
              ]"
            >
              {{
                s.value === null
                  ? '—'
                  : s.max === 100
                  ? s.value
                  : `${s.value}/${s.max}`
              }}
            </div>
            <div class="anna-subscore-label">{{ s.label }}</div>
          </div>
        </div>
      </div>
      <div class="anna-review-note-row">
        <p class="anna-review-note">
          <template v-if="overallLine">{{ overallLine }}<br /></template>
          ANNA's scores are suggestions: nothing happens to the candidate until
          someone uses the buttons above.
        </p>
        <oxd-button
          v-if="canAct"
          display-type="text"
          :label="scoring ? 'ANNA is reading the CV…' : 'Re-score CV'"
          :disabled="scoring || annaDown"
          @click="scoreCv"
        />
      </div>

      <!-- HR's turn: they missed the interview, or wrote to the jobs mailbox (CandidateStageService) -->
      <div
        v-if="
          review.stage &&
          review.stage.attention &&
          review.stage.key !== 'interviewed'
        "
        :class="['anna-attention', `anna-attention--${review.stage.key}`]"
      >
        <div class="anna-attention-head">
          <span :class="['anna-stage', `anna-tone--${stageTone}`]">{{
            review.stage.label
          }}</span>
          <span v-if="canAct" class="anna-attention-actions">
            <oxd-button
              v-if="
                ['missed', 'problem'].includes(review.stage.key) || wantsNewTime
              "
              display-type="secondary"
              :label="sending === 'again' ? 'Sending…' : 'Send new times'"
              :disabled="!!sending || annaDown"
              @click="sendEmail('again')"
            />
            <oxd-button
              v-if="review.stage.key === 'answers'"
              display-type="secondary"
              :label="sending === 'booking' ? 'Sending…' : 'Send the times'"
              :disabled="!!sending || annaDown"
              @click="sendEmail('booking')"
            />
            <oxd-button
              v-if="openMails.length"
              display-type="ghost"
              label="Mark as handled"
              :disabled="busyHandled"
              @click="markHandled"
            />
          </span>
        </div>
        <p v-if="review.stage.key === 'answers'" class="anna-attention-text">
          An answer to the initial questions misses a requirement, is unclear,
          or is still missing after Anna asked again, so no times went out.
          "Send the times" lets them book anyway.
        </p>
        <p v-if="review.stage.key === 'problem'" class="anna-attention-text">
          Anna couldn't run this interview (her side, not the candidate's).
          "Send new times" frees their booking and emails Anna's times again.
        </p>
        <p v-if="review.stage.key === 'paused'" class="anna-attention-text">
          The interview stopped again after Anna's times to continue it: the
          review below covers both calls. Shortlist, reject, or contact them.
        </p>
        <p v-if="review.stage.key === 'missed'" class="anna-attention-text">
          The booked time passed and the candidate never joined. "Send new
          times" frees their booking and emails Anna's times again; a reply from
          them shows here.
        </p>
        <div v-for="m in openMails" :key="m.id" class="anna-mail">
          <div class="anna-mail-head">
            <b>{{ m.subject || '(no subject)' }}</b>
            <span class="anna-muted">{{ date(m.receivedAt) }}</span>
          </div>
          <p v-if="m.summary" class="anna-mail-summary">
            ANNA: {{ m.summary }}
          </p>
          <details>
            <summary class="anna-muted">The email</summary>
            <pre class="anna-mail-body">{{ m.body }}</pre>
          </details>
        </div>
      </div>

      <!-- Tabs -->
      <div class="anna-tabs" role="tablist">
        <button
          v-for="t in tabs"
          :key="t.key"
          type="button"
          role="tab"
          :aria-selected="tab === t.key"
          :class="['anna-tab', {'anna-tab--active': tab === t.key}]"
          @click="tab = t.key"
        >
          {{ t.label }}
        </button>
      </div>

      <!-- Profile -->
      <div v-if="tab === 'profile'" class="anna-tab-body">
        <section v-if="review.summary">
          <h6 class="anna-h">Summary</h6>
          <p class="anna-text">{{ review.summary }}</p>
        </section>
        <section class="anna-facts">
          <div v-for="f in facts" :key="f.label" class="anna-fact">
            <div class="anna-fact-label">{{ f.label }}</div>
            <div class="anna-fact-val">{{ f.value || '—' }}</div>
          </div>
        </section>
        <!-- only for a candidate without a vacancy: one who applied is scored for that one (the CV score) -->
        <section v-if="!candidate.vacancy && review.vacancyMatches.length">
          <h6 class="anna-h">Vacancy matches</h6>
          <div v-for="m in shownMatches" :key="m.name" class="anna-row">
            <span>{{ m.name }}</span>
            <span :class="['anna-score', `anna-tone--${tone(m.score)}`]">
              {{ m.score }}{{ m.label ? ` (${m.label})` : '' }}
            </span>
          </div>
          <button
            v-if="review.vacancyMatches.length > 3"
            type="button"
            class="anna-more"
            @click="allMatches = !allMatches"
          >
            {{
              allMatches
                ? 'Show fewer'
                : `Show all (${review.vacancyMatches.length})`
            }}
          </button>
        </section>
        <div class="anna-two-col">
          <section>
            <h6 class="anna-h">Strengths</h6>
            <ul class="anna-list">
              <li v-for="s in review.strengths" :key="s">{{ s }}</li>
            </ul>
          </section>
          <section>
            <h6 class="anna-h">Weaknesses</h6>
            <ul class="anna-list">
              <li v-for="w in review.weaknesses" :key="w">{{ w }}</li>
            </ul>
          </section>
        </div>
        <section v-if="review.inconsistencies?.length">
          <h6 class="anna-h">Inconsistencies to check</h6>
          <ul class="anna-list anna-list--warn">
            <li v-for="x in review.inconsistencies" :key="x">{{ x }}</li>
          </ul>
        </section>
        <section v-if="review.coreSkills.length">
          <h6 class="anna-h">Core skills</h6>
          <div class="anna-skills">
            <div
              v-for="c in review.coreSkills"
              :key="c.label"
              class="anna-row anna-row--top"
            >
              <div>
                <div>{{ c.label }}</div>
                <div v-if="c.evidence" class="anna-quote">
                  “{{ c.evidence }}”
                </div>
              </div>
              <span class="anna-skill-value">
                <span
                  :class="['anna-check', `anna-tone--${checkTone(c.value)}`]"
                >
                  {{ c.value }}
                </span>
                <span
                  v-if="c.basis && c.basis !== 'stated'"
                  class="anna-basis"
                  :title="basisHelp[c.basis]"
                >
                  {{ c.basis }}
                </span>
              </span>
            </div>
          </div>
        </section>
      </div>

      <!-- Initial questions: HR emails them (Anna doesn't ask them); ANNA reads the reply -->
      <div v-show="tab === 'initial'" class="anna-tab-body">
        <!-- the verdict first: the answers, two to a row; the emails and the reply below -->
        <template v-if="review.answers.length">
          <div class="anna-row">
            <h6 class="anna-h">
              Answers
              <span class="anna-muted anna-h-note">
                {{
                  review.answersSource === 'interview'
                    ? 'from an HR interview call, before the questions were emailed'
                    : 'from the reply'
                }}{{
                  review.dates.answered && review.answersSource !== 'interview'
                    ? `, read on ${date(review.dates.answered)}`
                    : ''
                }}
              </span>
            </h6>
            <span
              v-if="review.scores.answers !== null"
              :class="[
                'anna-score',
                `anna-tone--${tone(review.scores.answers)}`,
              ]"
            >
              {{ review.scores.answers }}/100
            </span>
          </div>
          <p v-if="review.answersSummary" class="anna-text">
            {{ review.answersSummary }}
          </p>
          <div class="anna-answers">
            <div
              v-for="(a, i) in review.answers"
              :key="`s${i}`"
              class="anna-answer"
            >
              <span
                :class="['anna-flag', `anna-flag--${a.flag || 'grey'}`]"
                :title="flagWord(a.flag)"
              ></span>
              <div>
                <div class="anna-answer-q">{{ a.question }}</div>
                <div class="anna-text">{{ a.answer }}</div>
                <div v-if="a.evidence" class="anna-quote">
                  “{{ a.evidence }}”
                </div>
              </div>
            </div>
          </div>
        </template>
        <p v-if="initialError" class="anna-muted">{{ initialError }}</p>
        <p v-else-if="!initial" class="anna-muted">Loading the emails…</p>
        <template v-else>
          <p
            v-if="initial.testMode"
            class="anna-muted"
            title='ANNA reads the reply to message 1 by herself: when every answer checks out, message 2 goes by itself (with "Send the emails automatically" on in ANNA Settings → Emails).'
          >
            Test mode: the emails go to
            {{ initial.to || 'the test address (not set)' }}, not the candidate.
          </p>
          <div v-for="m in initialEmails" :key="m.key" class="anna-email">
            <span class="anna-email-title">
              Message {{ m.n }} · {{ m.title }}
            </span>
            <span
              :class="[
                'anna-email-status',
                {'anna-email-status--sent': m.sent},
              ]"
            >
              {{
                m.sent
                  ? `Sent ${m.sent.sentAt.slice(0, 16)} to ${m.sent.to}${
                      m.sent.test ? ' (test)' : ''
                    }`
                  : 'Not sent yet'
              }}
            </span>
            <span class="anna-email-actions">
              <oxd-button
                v-if="canAct"
                display-type="secondary"
                size="medium"
                :label="
                  sending === m.key
                    ? 'Sending…'
                    : m.sent
                    ? 'Send again'
                    : `Send message ${m.n}`
                "
                :disabled="!!sending || annaDown"
                @click="sendEmail(m.key)"
              />
              <oxd-button
                display-type="ghost"
                size="medium"
                label="Copy text"
                @click="copyEmail(m)"
              />
            </span>
            <details class="anna-fold anna-email-preview">
              <summary>Preview</summary>
              <div class="anna-email-subject">Subject: {{ m.subject }}</div>
              <div class="anna-email-body">{{ m.text }}</div>
            </details>
          </div>
          <!-- the list Email 1 asks, for every candidate (also on ANNA Settings → Emails) -->
          <details v-if="canAct" class="anna-fold" @toggle="onQuestionsToggle">
            <summary>
              Questions for everyone (Email 1)
              <span class="anna-muted">not just this candidate</span>
            </summary>
            <p class="anna-muted">
              One per line, in order. The question itself is bold (as written
              with **, else up to its question mark); an _italic note_ at its
              end goes on its own line. A question that names DeskTime rejects
              on a clear no. Saving changes Email 1 for everyone from now on;
              emails already sent stay as they were.
            </p>
            <p v-if="questionsText === null" class="anna-muted">Loading…</p>
            <template v-else>
              <oxd-input-field
                v-model="questionsText"
                type="textarea"
                class="anna-questions-edit"
                :disabled="questionsBusy"
              />
              <div class="anna-action-row">
                <oxd-button
                  display-type="secondary"
                  :label="questionsBusy ? 'Saving…' : 'Save the questions'"
                  :disabled="questionsBusy || annaDown || !questionsText.trim()"
                  @click="saveQuestions"
                />
              </div>
            </template>
          </details>
          <details v-if="initial.fromCv.length" class="anna-fold">
            <summary>
              Not asked: the CV answers them ({{ initial.fromCv.length }})
            </summary>
            <div
              v-for="(k, i) in initial.fromCv"
              :key="`cv${i}`"
              class="anna-answer"
            >
              <div>
                <div class="anna-answer-q">{{ k.question }}</div>
                <div class="anna-text">{{ k.answer }}</div>
                <div v-if="k.evidence" class="anna-quote">
                  “{{ k.evidence }}”
                </div>
              </div>
            </div>
          </details>
        </template>

        <!-- open until there are answers; the mailbox fills the reply in by itself -->
        <details class="anna-fold" :open="!review.answers.length">
          <summary>
            The candidate's reply
            <span class="anna-muted">(filled in by itself; or paste one)</span>
          </summary>
          <oxd-input-field
            v-model="reply"
            type="textarea"
            placeholder="Paste the candidate's email reply here"
            :disabled="readingReply"
          />
          <div v-if="canAct" class="anna-action-row">
            <oxd-button
              display-type="secondary"
              :label="readingReply ? 'ANNA is reading…' : 'Read the reply'"
              :disabled="readingReply || annaDown || !reply.trim()"
              title="ANNA checks each answer against its question. Reading it again replaces the result."
              @click="readReply"
            />
          </div>
        </details>
      </div>

      <!-- HR / Technical interview -->
      <div
        v-for="iv in interviews"
        v-show="tab === iv.key"
        :key="iv.key"
        class="anna-tab-body"
      >
        <p v-if="syncing && iv.slot === 'general'" class="anna-muted">
          Checking for meeting calls…
        </p>
        <!-- the next interview (its questions and a test call) first, then the calls so far -->
        <div v-if="iv.data" class="anna-callpicks" role="tablist">
          <button
            type="button"
            role="tab"
            :aria-selected="iv.showNext"
            :class="['anna-callpick', {'anna-callpick--active': iv.showNext}]"
            @click="pickedCalls[iv.slot] = 'next'"
          >
            Next interview · questions &amp; test call
          </button>
          <span
            v-for="call in iv.calls"
            :key="call.callId"
            :class="[
              'anna-callpick',
              'anna-callpick--call',
              {'anna-callpick--active': !iv.showNext && iv.picked === call},
            ]"
          >
            <button
              type="button"
              role="tab"
              class="anna-callpick-pick"
              :aria-selected="!iv.showNext && iv.picked === call"
              @click="pickedCalls[iv.slot] = call.callId"
            >
              {{ callTitle(call) }}
            </button>
            <button
              type="button"
              class="anna-callpick-delete"
              :disabled="deletingCall === call.callId"
              :title="`Delete this call (${callTitle(call)})`"
              :aria-label="`Delete ${callTitle(call)}`"
              @click="deleteCall(iv, call)"
            >
              ×
            </button>
          </span>
          <!-- an interview from before calls were kept one by one -->
          <button
            v-if="!iv.calls.length"
            type="button"
            role="tab"
            :aria-selected="!iv.showNext"
            :class="['anna-callpick', {'anna-callpick--active': !iv.showNext}]"
            @click="pickedCalls[iv.slot] = null"
          >
            {{ iv.title }}
          </button>
        </div>
        <template v-if="iv.data && !iv.showNext">
          <!-- what a reviewer needs first: the verdict, then the why, then the recordings -->
          <div class="anna-row">
            <h6 class="anna-h">
              {{ iv.picked ? callTitle(iv.picked) : iv.title }}
              <span class="anna-muted anna-h-note">
                {{
                  [
                    iv.date,
                    iv.data.durationMinutes
                      ? `${iv.data.durationMinutes} min`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')
                }}
              </span>
            </h6>
            <span
              v-if="iv.isLatest"
              :class="['anna-score', `anna-tone--${tone(iv.score, 10)}`]"
            >
              {{ iv.score ?? '—' }}/10
            </span>
          </div>
          <p v-if="!iv.isLatest" class="anna-muted">
            An earlier call. The scores are from the latest one.
          </p>
          <p v-if="iv.data.test" class="anna-muted">
            Test interview from this page, not a real candidate call. Not scored
            yet.
          </p>
          <p v-if="iv.data.summary" class="anna-text">{{ iv.data.summary }}</p>
          <div
            v-if="
              iv.data.concerns?.length || iv.data.candidateQuestions?.length
            "
            class="anna-two-col"
          >
            <section v-if="iv.data.concerns?.length">
              <h6 class="anna-h">Concerns / follow-up</h6>
              <ul class="anna-list">
                <li v-for="c in iv.data.concerns" :key="c">{{ c }}</li>
              </ul>
            </section>
            <section v-if="iv.data.candidateQuestions?.length">
              <h6 class="anna-h">Questions the candidate asked</h6>
              <ul class="anna-list">
                <li v-for="q in iv.data.candidateQuestions" :key="q">
                  {{ q }}
                </li>
              </ul>
            </section>
          </div>
          <!-- why the score: David's phases, each out of 10, a line each -->
          <template v-if="iv.data.phases?.length">
            <h6 class="anna-h anna-h--spaced">Score by phase</h6>
            <div v-for="p in iv.data.phases" :key="p.phase" class="anna-phase">
              <span class="anna-phase-name">{{ phaseNames[p.phase] }}</span>
              <span class="anna-phase-bar">
                <i
                  :class="`anna-tone--${tone(p.score, 10)}`"
                  :style="{width: `${(p.score ?? 0) * 10}%`}"
                ></i>
              </span>
              <span :class="['anna-score', `anna-tone--${tone(p.score, 10)}`]">
                {{ p.score ?? '—' }}/10
              </span>
              <span
                class="anna-phase-why"
                :title="p.score === null ? '' : p.why"
              >
                {{ p.score === null ? 'Not reached in this call.' : p.why }}
              </span>
            </div>
          </template>
          <!-- the practical questions Anna asked in this call -->
          <template v-if="iv.data.screening?.length">
            <h6 class="anna-h anna-h--spaced">Practical questions</h6>
            <p v-if="iv.data.screeningSummary" class="anna-text">
              {{ iv.data.screeningSummary }}
            </p>
            <div class="anna-answers">
              <div
                v-for="(a, i) in iv.data.screening"
                :key="`p${i}`"
                class="anna-answer"
              >
                <span
                  :class="['anna-flag', `anna-flag--${a.flag || 'grey'}`]"
                  :title="flagWord(a.flag)"
                ></span>
                <div>
                  <div class="anna-answer-q">{{ a.question }}</div>
                  <div class="anna-text">
                    {{ a.answer || 'Not answered in the call.' }}
                  </div>
                  <div v-if="a.evidence" class="anna-quote">
                    “{{ a.evidence }}”
                  </div>
                </div>
              </div>
            </div>
          </template>
          <!-- calls from before the screening questions were emailed -->
          <details v-else-if="iv.picked?.answers?.length" class="anna-fold">
            <summary>
              Screening answers in this call ({{ iv.picked.answers.length }})
            </summary>
            <div
              v-for="(a, i) in iv.picked.answers"
              :key="`a${i}`"
              class="anna-answer"
            >
              <div>
                <div class="anna-answer-q">{{ a.question }}</div>
                <div class="anna-text">{{ a.answer }}</div>
              </div>
            </div>
          </details>
          <!-- the recording and a small video beside it; the video grows on demand, fullscreen from its controls -->
          <div v-if="iv.hasRecording || iv.picked?.hasVideo" class="anna-media">
            <audio
              v-if="iv.hasRecording"
              class="anna-audio"
              controls
              preload="metadata"
              :src="recordingUrl(iv.slot, iv.picked?.callId)"
            ></audio>
            <div
              v-if="iv.picked?.hasVideo"
              :class="['anna-video-box', {'anna-video-box--big': bigVideo}]"
            >
              <video
                class="anna-video"
                controls
                preload="metadata"
                :src="recordingUrl(iv.slot, `${iv.picked.callId}-video`)"
              ></video>
              <button
                type="button"
                class="anna-video-size"
                :title="
                  bigVideo ? 'Make the video smaller' : 'Make the video bigger'
                "
                @click="bigVideo = !bigVideo"
              >
                <i
                  :class="[
                    'bi',
                    bigVideo
                      ? 'bi-arrows-angle-contract'
                      : 'bi-arrows-angle-expand',
                  ]"
                  aria-hidden="true"
                />
                {{ bigVideo ? 'Smaller' : 'Bigger' }}
              </button>
            </div>
          </div>
          <details v-if="iv.data.transcript?.length" class="anna-fold">
            <summary>
              Transcript ({{ iv.data.transcript.length }} lines)
            </summary>
            <div class="anna-action-row">
              <oxd-button
                display-type="ghost"
                size="medium"
                label="Copy transcript"
                @click="copyTranscript(iv)"
              />
            </div>
            <div class="anna-transcript">
              <div
                v-for="(line, i) in iv.data.transcript"
                :key="i"
                :class="[
                  'anna-transcript-line',
                  `anna-transcript-line--${line.role}`,
                ]"
              >
                <span class="anna-transcript-who">
                  {{ line.role === 'anna' ? iv.persona : 'Candidate' }}
                </span>
                <template v-for="(part, j) in markSlurs(line.text)" :key="j">
                  <mark v-if="part.slur" class="anna-slur">{{
                    part.text
                  }}</mark>
                  <template v-else>{{ part.text }}</template>
                </template>
              </div>
            </div>
          </details>
          <details v-if="iv.data.questions?.length" class="anna-fold">
            <summary>
              Answers to {{ iv.persona }}'s questions ({{
                iv.data.questions.length
              }})
            </summary>
            <div
              v-for="(q, i) in iv.data.questions"
              :key="i"
              class="anna-answer"
            >
              <div>
                <div class="anna-answer-q">{{ q.question }}</div>
                <div class="anna-text">{{ q.answer }}</div>
              </div>
            </div>
          </details>
          <div v-if="iv.data.callId && canAct" class="anna-action-row">
            <oxd-button
              display-type="ghost"
              :label="reviewing ? 'ANNA is reviewing…' : 'Analyse again'"
              :disabled="reviewing"
              title="Fetches the call from Vapi again: transcript, recording and ANNA's review."
              @click="reviewAgain(iv)"
            />
          </div>
        </template>
        <p v-else-if="!iv.data" class="anna-muted">
          No {{ iv.title }} yet. Below: what {{ iv.persona }} will ask, and a
          test call you can make from this page.
        </p>
        <!-- what the next call will ask, and a test call; v-show keeps a live test call
             going while HR looks at another call -->
        <div v-show="iv.showNext">
          <test-interview
            :key="iv.slot"
            :plan-version="planVersion"
            :candidate-id="candidate.id"
            :interview-slot="iv.slot"
            :persona="iv.persona"
            :anna-down="annaDown"
            @saved="onTestSaved(iv.slot, $event)"
          />
        </div>
      </div>
      <!-- Documents: letters to the candidate (offers, rejections…), loaded on opening the tab -->
      <div
        v-if="seenDocuments"
        v-show="tab === 'documents'"
        class="anna-tab-body"
      >
        <candidate-letters
          :candidate-id="candidate.id"
          :can-act="canAct"
          @moved="reloadPage"
        />
      </div>
      <!-- Details / History: Orbit's own candidate form and action log -->
      <div v-show="tab === 'details'" class="anna-tab-body anna-embedded">
        <slot name="details"></slot>
      </div>
      <div v-show="tab === 'history'" class="anna-tab-body anna-embedded">
        <slot name="history"></slot>
      </div>
    </template>
  </div>
  <!-- no review to put them in tabs: shown below, as before -->
  <template v-if="!tabbed">
    <slot name="details"></slot>
    <slot name="history"></slot>
  </template>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import useLocale from '@/core/util/composable/useLocale';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {formatDate, parseDate} from '@ohrm/core/util/helper/datefns';
import TestInterview from './TestInterview.vue';
import CandidateLetters from './CandidateLetters.vue';
import {
  scoreTone,
  checkTone,
  stageTone,
  CALL_SOURCES,
  recordingUrl,
  copyText,
} from '../util/review';
import {markSlurs} from '../util/slurs';
import {annaStatus} from '../util/status';

// ANNA's review on the stock candidate page (ViewCandidateProfile): score header
// and the design's four tabs. Read only; the stock status actions stay the way
// anything happens to a candidate.

export default {
  name: 'AnnaCandidateReview',
  components: {
    'test-interview': TestInterview,
    'candidate-letters': CandidateLetters,
  },
  props: {
    candidate: {
      type: Object,
      required: true,
    },
  },
  // the review, each time it's (re)loaded: the page shares bits of it with the candidate card
  emits: ['review'],
  setup() {
    const {locale} = useLocale();
    const {jsDateFormat} = useDateFormat();
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/anna/candidates',
    );
    // fetching meeting calls is quiet: a failure only means "try again next time"
    const syncHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/anna/candidates',
    );
    syncHttp.setIgnorePath('.*');
    return {http, syncHttp, locale, jsDateFormat};
  },
  data() {
    return {
      isLoading: true,
      scoring: false,
      // Admin acts (scores, sends, reads replies); the other roles only read
      canAct: false,
      // the meeting video at full width (else small, beside the recording)
      bigVideo: false,
      basisHelp: {
        inferred:
          'Not stated in the CV: ANNA inferred it from context. Check it.',
        'self-reported':
          'Only named in the CV (a skills list, a keyword or a self-rating): no work or result shows it.',
      },
      reviewing: false,
      busyHandled: false,
      // bumped when the CV is re-scored, so "Anna will ask" reloads
      planVersion: 0,
      review: null,
      // the review couldn't be loaded: Orbit's own candidate page still works
      unavailable: false,
      // ANNA's server: {state, online, message}; null = unknown or not for this user
      status: null,
      // the call shown per interview; null = the latest
      pickedCalls: {general: null},
      // the call being deleted
      deletingCall: null,
      syncing: false,
      tab: 'profile',
      // the initial questions HR emails: {ask, fromCv} from ANNA, loaded on opening the tab
      initial: null,
      initialError: null,
      reply: '',
      readingReply: false,
      // the list Email 1 asks (ANNA Settings' script_screening), one per line; null until opened
      questionsText: null,
      questionsBusy: false,
      // which email is being sent: 'first' | 'booking' | 'again' | null
      sending: null,
      // the technical interview (Alex) and the questionnaire tabs come back when they are in use
      tabs: [
        {key: 'profile', label: 'ANNA review'},
        {key: 'initial', label: 'Initial questions'},
        {key: 'hr', label: 'HR interview'},
        {key: 'documents', label: 'Documents'},
        {key: 'details', label: 'Candidate details'},
        {key: 'history', label: 'History'},
      ],
      allMatches: false,
      // the Documents tab loads once it's first opened, then stays
      seenDocuments: false,
    };
  },
  computed: {
    phaseNames() {
      return {
        general: '1. General',
        resume: '2. The resume',
        personality: '3. Personality',
        role: '4. The role',
      };
    },
    // the two emails before the interview, as they'd go out, and when each was sent
    initialEmails() {
      return [
        {key: 'first', n: 1, title: 'Introduction and questions'},
        {key: 'booking', n: 2, title: 'Times to book the interview'},
      ]
        .filter((m) => this.initial?.emails?.[m.key])
        .map((m) => ({...m, ...this.initial.emails[m.key]}));
    },
    annaDown() {
      return !!this.status && !this.status.online;
    },
    // how the overall was made, in one line (CvScoringService::overall)
    overallLine() {
      const s = this.review.scores;
      const o = s.overallParts;
      if (!o)
        return s.hr === null
          ? 'Overall comes once Anna has interviewed them.'
          : '';
      return [
        `Overall: CV ${s.cv} × ${o.cvWeight}% + interview ${s.hr}/10 × ${o.interviewWeight}%`,
        o.partial ? ' (the interview counts less: not all of it was held)' : '',
        o.capped
          ? `; a failed interview keeps it under ${
              this.review.bands?.worthALook ?? 50
            }`
          : '',
        ` = ${s.overall}.`,
      ].join('');
    },
    subScores() {
      const s = this.review.scores;
      return [
        // the CV was scored for another vacancy, not open then: "Score again" scores it for this one
        {
          label: s.cvForOtherVacancy ? 'CV (other vacancy)' : 'CV',
          value: s.cv,
          max: 100,
        },
        {label: 'HR interview', value: s.hr, max: 10},
      ];
    },
    facts() {
      return [
        {label: 'Country, city', value: this.review.location},
        {label: 'English', value: this.englishText},
        // the vacancy applied for is in the status card; the best match only when it differs
        this.review.bestMatch &&
        this.review.bestMatch.name !== this.candidate.vacancy?.name
          ? {label: 'Best match', value: this.review.bestMatch.name}
          : null,
        {
          label: 'Salary expectations',
          value: this.review.salaryExpectation || 'Not yet provided',
        },
      ].filter(Boolean);
    },
    tabbed() {
      return !this.isLoading && !this.unavailable && !!this.review;
    },
    shownMatches() {
      return this.allMatches
        ? this.review.vacancyMatches
        : this.review.vacancyMatches.slice(0, 3);
    },
    // The CV's level is self-reported; the interview's is ANNA's reading of the transcript.
    englishText() {
      const cv = this.review.englishLevel;
      const heard = this.review.hrInterview?.englishLevel;
      if (!heard) return cv ? `${cv} (from CV)` : null;
      return [cv && `${cv} (CV)`, `${heard} (interview)`]
        .filter(Boolean)
        .join(' · ');
    },
    // what HR does next, in plain words
    // the candidate's emails HR hasn't dealt with yet
    openMails() {
      return (this.review.mails || []).filter((m) => !m.handled);
    },
    wantsNewTime() {
      return this.openMails.some((m) => m.intent === 'new_time');
    },
    stageTone() {
      return stageTone(this.review.stage);
    },
    nextStep() {
      const d = this.review.dates;
      const hr = this.review.scores.hr;
      if (d.hired) return `Hired on ${this.date(d.hired)}.`;
      if (d.jobOffered) return `Job offered on ${this.date(d.jobOffered)}.`;
      // where they are with Anna (CandidateStageService)
      const stage = this.review.stage?.key;
      if (stage === 'rejected') {
        return 'Rejected automatically by Anna: their reply to the initial questions says no to DeskTime (see History).';
      }
      if (stage === 'replied') {
        return 'They wrote to the jobs mailbox: read it below, answer them, then "Mark as handled".';
      }
      if (stage === 'problem') {
        return 'The interview didn\'t happen on Anna\'s side: "Send new times" below.';
      }
      if (stage === 'missed') {
        return 'They missed the interview with Anna: "Send new times" below, or reject them.';
      }
      if (stage === 'paused' && !this.review.stage.attention) {
        return 'The interview stopped before its end: Anna emailed times to continue it, and skips what was covered.';
      }
      if (stage === 'paused') {
        return 'The interview stopped twice: decide from the review below.';
      }
      if (stage === 'booked') {
        return `${this.review.stage.label}. Anna runs it; the result shows here after the call.`;
      }
      if (stage === 'held') {
        return "The interview time has passed: Anna's call shows here within minutes.";
      }
      if (stage === 'times_sent') {
        return "Anna's times are emailed: waiting for them to book.";
      }
      if (stage === 'questions') {
        return 'The initial questions are emailed: Anna sends the times once every answer checks out.';
      }
      if (stage === 'answers') {
        return 'Their answers need a look (Initial questions tab): "Send the times" below, or reject them.';
      }
      if (d.hrInterview) {
        return `Interviewed by Anna on ${this.date(d.hrInterview)}${
          hr !== null && hr !== undefined ? ` (${hr}/10)` : ''
        }: shortlist or reject, with the buttons above.`;
      }
      return 'The HR interview with Anna. "Schedule meeting" above books it; the HR interview tab shows what she will ask.';
    },
    interviews() {
      return this.interviewTabs().map((iv) => {
        const calls = (this.review.calls || []).filter(
          (c) => c.slot === iv.slot,
        );
        const picked =
          calls.find((c) => c.callId === this.pickedCalls[iv.slot]) ||
          calls[0] ||
          null;
        const isLatest = !picked || picked === calls[0];
        return {
          ...iv,
          showNext: !iv.data || this.pickedCalls[iv.slot] === 'next',
          calls,
          picked,
          isLatest,
          // the latest call is the review's own copy (with its score); an earlier one its history row
          data: isLatest ? iv.data : picked.interview,
          date: isLatest ? iv.date : this.date(picked.startedAt),
          hasRecording: picked
            ? picked.hasRecording
            : !!this.review.recordings?.[iv.slot],
        };
      });
    },
  },
  watch: {
    review(review) {
      this.$emit('review', review);
      if (this.reply === '' && review?.answersReply)
        this.reply = review.answersReply;
    },
    tab(tab) {
      if (tab === 'initial' && !this.initial) this.loadInitial();
      if (tab === 'documents') this.seenDocuments = true;
    },
    // a re-score can change which questions the CV answers
    planVersion() {
      this.initial = null;
      if (this.tab === 'initial') this.loadInitial();
    },
  },
  mounted() {
    // the Initial questions tab picks up replies the mailbox check reads, without F5
    this.refreshTimer = setInterval(this.refreshAnswers, 60000);
  },
  beforeUnmount() {
    clearInterval(this.refreshTimer);
  },
  beforeMount() {
    // ?tab=hr&call=<id> (from Interview recordings): open that interview with that call
    const query = new URLSearchParams(window.location.search);
    // old links: the questionnaire is Initial questions again; the removed technical tab opens the HR interview
    const wanted =
      {qa: 'initial', tech: 'hr'}[query.get('tab')] || query.get('tab');
    if (this.tabs.some((t) => t.key === wanted)) {
      this.tab = wanted;
    }
    if (query.get('call')) {
      this.pickedCalls.general = query.get('call');
    }
    annaStatus().then((status) => (this.status = status));
    this.loadReview().then(() => this.syncMeetingCalls());
  },
  methods: {
    markSlurs,
    interviewTabs() {
      return [
        {
          key: 'hr',
          slot: 'general',
          title: 'HR interview',
          persona: 'Anna',
          data: this.review.hrInterview,
          score: this.review.scores.hr,
          date: this.date(this.review.dates.hrInterview),
        },
      ];
    },
    loadReview() {
      return this.http
        .request({
          method: 'GET',
          url: `/api/v2/anna/candidates/${this.candidate.id}/review`,
        })
        .then(({data: {data, meta}}) => {
          this.canAct = !!meta?.canAct;
          this.review = data && data.updatedAt ? data : null;
        })
        .catch(() => {
          this.unavailable = true;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    // Anna's meeting calls are fetched once the meeting is over; it can take a minute (ANNA reviews them)
    syncMeetingCalls() {
      if (this.unavailable) return;
      this.syncing = true;
      this.syncHttp
        .request({
          method: 'GET',
          url: `/api/v2/anna/candidates/${this.candidate.id}/meeting-calls`,
          timeout: 300000,
        })
        .then(({data: {data}}) => {
          (data?.errors || []).forEach((message) =>
            this.$toast.warn({title: 'A meeting call was not saved', message}),
          );
          if (data?.saved > 0) {
            this.$toast.success({
              title: 'Meeting call saved',
              message: 'The meeting call is under the HR interview tab.',
            });
            return this.loadReview();
          }
        })
        .catch(() => null)
        .finally(() => {
          this.syncing = false;
        });
    },
    callTitle(call) {
      const when = call.startedAt
        ? `${this.date(call.startedAt)} ${String(call.startedAt).slice(11, 16)}`
        : '';
      return `${CALL_SOURCES[call.source] || 'Browser test'}${
        when ? ` · ${when}` : ''
      }`;
    },
    // as plain text, "Anna: …" / "Candidate: …" per line, for pasting elsewhere
    copyTranscript(iv) {
      const text = iv.data.transcript
        .map(
          (line) =>
            `${line.role === 'anna' ? iv.persona : 'Candidate'}: ${line.text}`,
        )
        .join('\n');
      copyText(this.$toast, text, 'The transcript is in your clipboard.');
    },
    // quietly, while the Initial questions tab is open and the questions were sent
    refreshAnswers() {
      if (
        this.tab !== 'initial' ||
        document.hidden ||
        this.readingReply ||
        this.sending ||
        !this.review?.dates.questionsSent
      ) {
        return;
      }
      this.syncHttp
        .request({
          method: 'GET',
          url: `/api/v2/anna/candidates/${this.candidate.id}/review`,
        })
        .then(({data: {data}}) => {
          if (data?.updatedAt && data.updatedAt !== this.review.updatedAt) {
            // a reply HR is pasting stays: only the one shown untouched is replaced
            const untouched = this.reply === (this.review.answersReply || '');
            if (data.answersReply && untouched) this.reply = data.answersReply;
            this.review = data;
          }
        })
        .catch(() => null);
    },
    // the questions are ANNA's setting: read on opening, saved for everyone
    settingsApi() {
      return new APIService(window.appGlobal.baseUrl, '/api/v2/anna/settings');
    },
    onQuestionsToggle(event) {
      if (!event.target.open || this.questionsText !== null) return;
      this.settingsApi()
        .request({method: 'GET'})
        .then(({data: {data}}) => {
          const field = (data.groups || [])
            .flatMap((g) => g.fields || [])
            .find((f) => f.key === 'script_screening');
          this.questionsText = (field?.value || []).join('\n');
        })
        .catch(() => {
          this.questionsText = '';
        });
    },
    saveQuestions() {
      this.questionsBusy = true;
      const list = this.questionsText
        .split('\n')
        .map((q) => q.trim())
        .filter(Boolean);
      this.settingsApi()
        .request({method: 'PUT', data: {settings: {script_screening: list}}})
        .then(() => {
          this.$toast.saveSuccess();
          this.questionsText = list.join('\n');
          // the messages above show the new list
          this.loadInitial();
        })
        .finally(() => {
          this.questionsBusy = false;
        });
    },
    // a letter changed the status: the page (status, History) shown anew
    reloadPage() {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', 'documents');
      setTimeout(() => window.location.assign(url.toString()), 1500);
    },
    loadInitial() {
      this.initialError = null;
      this.syncHttp
        .request({
          method: 'GET',
          url: `/api/v2/anna/candidates/${this.candidate.id}/answers`,
        })
        .then(({data: {data}}) => {
          this.initial = {
            ask: data.ask || [],
            fromCv: data.fromCv || [],
            to: data.to,
            testMode: data.testMode,
            emails: data.emails || {},
          };
        })
        .catch(() => {
          this.initialError =
            "The questions couldn't be loaded right now (ANNA unavailable, or only Admins can see them).";
        });
    },
    // subject and text, to send it by hand
    copyEmail(m) {
      copyText(
        this.$toast,
        `${m.subject}\n\n${m.text}`,
        'The email is in your clipboard.',
      );
    },
    // the candidate's emails dealt with: they leave "Needs attention"
    markHandled() {
      this.busyHandled = true;
      this.http
        .request({
          method: 'PUT',
          url: `/api/v2/anna/candidates/${this.candidate.id}/answers`,
          data: {handled: true},
        })
        .then(({data: {data}}) => {
          if (data && data.updatedAt) this.review = data;
          this.$toast.success({title: 'Marked as handled', message: ''});
        })
        .catch(() => null)
        .finally(() => {
          this.busyHandled = false;
        });
    },
    flagWord(flag) {
      return (
        {
          green: 'agrees',
          yellow: 'not sure',
          red: 'disagrees',
          grey: 'no answer',
        }[flag || 'grey'] || 'no answer'
      );
    },
    sendEmail(key) {
      // a real email to the candidate (not test mode): asked first, it can't be taken back
      if (
        this.initial?.testMode !== true &&
        !window.confirm(
          key === 'again'
            ? 'Email the candidate new times? Their booked interview, if any, is cancelled.'
            : 'Send this email to the candidate now?',
        )
      )
        return;
      this.sending = key;
      this.http
        .request({
          method: 'PUT',
          url: `/api/v2/anna/candidates/${this.candidate.id}/answers`,
          data: {send: key},
        })
        .then(({data: {data, meta}}) => {
          if (data && data.updatedAt) this.review = data;
          const email = meta?.email || {};
          const toast = {
            title: email.sent ? 'Sent' : 'Not sent',
            message:
              [email.sent ? `Emailed to ${email.to}.` : null, email.note]
                .filter(Boolean)
                .join(' ') || 'The email was not sent.',
          };
          if (email.sent) this.$toast.success(toast);
          else this.$toast.warn(toast);
          this.loadInitial();
        })
        .catch(() => null)
        .finally(() => {
          this.sending = null;
        });
    },
    // takes 10-30 s: ANNA's reply reader (Vapi) reads it. Errors come back as the API's toast.
    readReply() {
      this.readingReply = true;
      this.http
        .request({
          method: 'PUT',
          url: `/api/v2/anna/candidates/${this.candidate.id}/answers`,
          data: {reply: this.reply},
          timeout: 180000,
        })
        .then(({data: {data}}) => {
          if (data && data.updatedAt) this.review = data;
          this.$toast.success({
            title: 'Read',
            message: 'ANNA has checked the answers.',
          });
        })
        .catch(() => null)
        .finally(() => {
          this.readingReply = false;
        });
    },
    // a test call just ended: show it
    onTestSaved(slot, review) {
      this.pickedCalls[slot] = null;
      this.onInterviewSaved(review);
    },
    onInterviewSaved(review) {
      if (review && review.updatedAt) this.review = review;
      (review?.notes || []).forEach((message) =>
        this.$toast.warn({title: 'Partly saved', message}),
      );
    },
    // a test call, or a meeting call, gone for good: its transcript, recording and review
    deleteCall(iv, call) {
      const shown = iv.data?.callId === call.callId;
      if (
        !window.confirm(
          `Delete "${this.callTitle(
            call,
          )}"? Its transcript, recording and review go for good.` +
            (shown
              ? " The candidate's HR interview score goes back to the previous call's, or is cleared."
              : ''),
        )
      ) {
        return;
      }
      this.deletingCall = call.callId;
      this.http
        .request({
          method: 'DELETE',
          url: `/api/v2/anna/calls/${encodeURIComponent(call.callId)}`,
        })
        .then(({data: {data}}) => {
          if (this.pickedCalls[iv.slot] === call.callId) {
            this.pickedCalls[iv.slot] = null;
          }
          if (data && data.updatedAt) this.review = data;
          this.$toast.success({title: 'Deleted', message: 'The call is gone.'});
        })
        .catch(() => null)
        .finally(() => {
          this.deletingCall = null;
        });
    },
    recordingUrl(slot, callId) {
      return recordingUrl(this.candidate.id, slot, callId);
    },
    // Same as the end of a call: fetch it from Vapi again, keep the recording, re-review.
    reviewAgain(iv) {
      this.reviewing = true;
      this.http
        .request({
          method: 'PUT',
          url: `/api/v2/anna/candidates/${this.candidate.id}/interview/${iv.slot}`,
          data: {callId: iv.data.callId},
          timeout: 180000,
        })
        .then(({data: {data}}) => {
          this.onInterviewSaved(data);
          if (!data?.notes?.length) {
            return this.$toast.success({
              title: 'Reviewed',
              message: 'ANNA has read the interview.',
            });
          }
        })
        .catch(() => null)
        .finally(() => {
          this.reviewing = false;
        });
    },
    // Takes 20-60 s: ANNA's CV screening (Vapi) reads the CV. Errors come back as the API's toast.
    scoreCv() {
      this.scoring = true;
      this.http
        .request({
          method: 'PUT',
          url: `/api/v2/anna/candidates/${this.candidate.id}/score`,
          timeout: 180000,
        })
        .then(({data: {data}}) => {
          this.review = data && data.updatedAt ? data : this.review;
          this.planVersion++;
          // the new questions are what HR wants to see now
          this.pickedCalls.general = 'next';
          if (this.review?.hrPlan) {
            this.$toast.info({
              title: 'Your edited plan is kept',
              message:
                'The HR interview tab still uses the plan HR edited. "Back to ANNA\'s plan" there shows the new questions.',
            });
          }
          return this.$toast.success({
            title: 'Scored',
            message: 'ANNA has read the CV.',
          });
        })
        .catch(() => null)
        .finally(() => {
          this.scoring = false;
        });
    },
    tone(value, max = 100) {
      return scoreTone(value, max, this.review?.bands);
    },
    checkTone,
    date(value) {
      if (!value) return null;
      return formatDate(
        parseDate(String(value).slice(0, 10)),
        this.jsDateFormat,
        {
          locale: this.locale,
        },
      );
    },
  },
};
</script>

<style lang="scss" scoped src="./anna-tones.scss"></style>
<style lang="scss" scoped>
.anna-review {
  margin-top: 1.2rem;
}
.anna-offline {
  margin: 0 0 12px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  color: #a67c00;
  background: #fdf6d8;
}
.anna-review-empty,
.anna-muted {
  color: #6a7482;
  font-size: 13px;
}
.anna-review-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 24px;
}
.anna-review-score {
  display: flex;
  align-items: center;
  gap: 14px;
}
.anna-overall-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64728c;
}
.anna-overall {
  font-family: 'Sora', sans-serif;
  font-size: 30px;
  font-weight: 800;
  padding: 6px 14px;
  border-radius: 14px;
  span {
    font-size: 13px;
    font-weight: 700;
    opacity: 0.7;
  }
}
.anna-review-label,
.anna-subscore-label,
.anna-fact-label {
  font-size: 11px;
  font-weight: 700;
  color: #9aa4b1;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 4px;
}
.anna-subscores {
  display: flex;
  gap: 18px;
}
.anna-subscore {
  text-align: center;
}
.anna-subscore-val {
  font-weight: 800;
  font-size: 15px;
  padding: 4px 10px;
  border-radius: 10px;
}
.anna-row--top {
  align-items: flex-start;
}
.anna-skill-value {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
  flex-shrink: 0;
}
.anna-attention {
  margin: 0 0 16px;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid #f1e3b0;
  background: #fffbea;
  &--missed {
    border-color: #f3cfc9;
    background: #fff5f3;
  }
}
.anna-attention-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
}
.anna-attention-actions {
  display: flex;
  gap: 8px;
}
.anna-attention-text,
.anna-mail-summary {
  margin: 8px 0 0;
  font-size: 13px;
}
.anna-mail {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}
.anna-mail-head {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
}
.anna-mail-body {
  white-space: pre-wrap;
  font: inherit;
  font-size: 13px;
  margin: 6px 0 0;
  max-height: 240px;
  overflow: auto;
}
.anna-basis {
  font-size: 10px;
  font-weight: 700;
  color: #a67c00;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  cursor: help;
}
.anna-quote {
  margin-top: 2px;
  font-size: 12px;
  font-style: italic;
  color: #6a7482;
}
.anna-list--warn li::marker {
  color: #a67c00;
}
.anna-audio {
  width: 100%;
  max-width: 420px;
}
.anna-video {
  display: block;
  width: 100%;
  border-radius: 8px;
  background: #000;
}
.anna-video-box {
  position: relative;
  width: 240px;
  &--big {
    flex-basis: 100%;
    width: 100%;
    max-width: 960px;
  }
}
.anna-video-size {
  margin-top: 4px;
  border: 0;
  background: none;
  padding: 0;
  font-size: 12px;
  color: #1f5fa8;
  cursor: pointer;
  .bi {
    margin-right: 4px;
  }
}
.anna-transcript {
  max-height: 320px;
  overflow-y: auto;
  padding: 12px 14px;
  border: 1px solid #e7ebf1;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.anna-transcript-line {
  font-size: 13px;
  line-height: 1.5;
  &--candidate {
    color: #1f5fa8;
  }
}
// marked, not censored: the reviewer sees the word as said
.anna-slur {
  background: #fde2e1;
  color: #b42318;
  font-weight: 700;
  border-radius: 3px;
  padding: 0 2px;
}
.anna-transcript-who {
  font-weight: 800;
  margin-right: 6px;
}
.anna-review-start {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.anna-review-note-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  .anna-review-note {
    margin: 14px 0 0;
  }
}
.anna-review-note {
  margin: 14px 0 0;
  font-size: 12px;
  color: #9aa4b1;
}
.anna-callpicks {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.anna-callpick.anna-callpick--call {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 0 4px 0 0;
  cursor: default;
}
.anna-callpick-pick,
.anna-callpick-delete {
  border: 0;
  background: none;
  font: inherit;
  color: inherit;
  line-height: normal;
  min-height: 0;
  cursor: pointer;
}
.anna-callpick-pick {
  height: auto;
  padding: 4px 2px 4px 12px;
}
.anna-callpick-delete {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  padding: 0;
  line-height: 18px;
  font-size: 14px;
  color: #9aa4b1;
  &:hover,
  &:focus-visible {
    background: #fdecec;
    color: #c0392b;
  }
  &:disabled {
    opacity: 0.4;
    cursor: wait;
  }
}
.anna-callpick {
  border: 1px solid #dfe4ec;
  background: #fff;
  border-radius: 14px;
  padding: 4px 12px;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: #64728c;
  cursor: pointer;
  &--active {
    border-color: #1f5fa8;
    color: #1f5fa8;
    background: #eef4fb;
  }
}
.anna-more {
  margin-top: 6px;
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  color: #1f5fa8;
  cursor: pointer;
}
.anna-skills {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  column-gap: 32px;
}
// Orbit's own cards inside a tab: no card in a card, no repeated title
.anna-embedded :deep(.orangehrm-background-container),
.anna-embedded :deep(.orangehrm-paper-container),
.anna-embedded :deep(.orangehrm-card-container) {
  padding: 0;
  box-shadow: none;
  background: none;
  margin: 0;
}
.anna-embedded :deep(.orangehrm-main-title) {
  display: none;
}
.anna-next {
  max-width: 460px;
}
.anna-next-text {
  font-size: 13px;
  line-height: 1.5;
  color: #3d4757;
}
.anna-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 18px;
  border-bottom: 1px solid #e7ebf1;
}
.anna-tab {
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  padding: 10px 14px;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  color: #6a7482;
  cursor: pointer;
  &--active {
    color: #1f5fa8;
    border-bottom-color: #1f5fa8;
  }
}
.anna-tab-body {
  padding-top: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
// a block that opens on click (native <details>): long texts stay one line until wanted
.anna-fold > summary {
  cursor: pointer;
  list-style: none;
  font-size: 13px;
  font-weight: 700;
  color: #12233f;
  &::-webkit-details-marker {
    display: none;
  }
  &::before {
    content: '▸ ';
    color: #64728c;
  }
}
.anna-fold[open] > summary {
  margin-bottom: 8px;
  &::before {
    content: '▾ ';
  }
}
// the six questions at a glance, a long list scrolls
.anna-questions-edit :deep(textarea) {
  min-height: 220px;
  max-height: 50vh;
  resize: vertical;
}
// a note after a heading: who, when, from where
.anna-h-note {
  margin-left: 8px;
  font-weight: 400;
}
// answers two to a row, a narrow page one
.anna-answers {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  column-gap: 24px;
}
// the recording and the small video on one line; the bigger video on its own below
.anna-media {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 24px;
}
.anna-h {
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 800;
  color: #12233f;
  &--spaced {
    margin-top: 6px;
  }
}
.anna-text {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: #16202b;
}
.anna-facts {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 14px 24px;
}
.anna-fact-val {
  font-size: 13px;
  font-weight: 600;
  color: #16202b;
}
.anna-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 0;
  border-bottom: 1px solid #f2f4f7;
  font-size: 13px;
  .anna-h {
    margin: 0;
  }
}
.anna-two-col {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 18px 32px;
}
.anna-list {
  margin: 0;
  padding-left: 18px;
  font-size: 13px;
  line-height: 1.7;
}
.anna-action-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}
.anna-answer {
  display: flex;
  gap: 10px;
  padding: 5px 0;
  border-bottom: 1px solid #f2f4f7;
}
.anna-answer-q {
  font-size: 13px;
  font-weight: 700;
  color: #12233f;
  margin-bottom: 2px;
}
.anna-phase {
  display: grid;
  grid-template-columns: 130px 160px 52px 1fr;
  align-items: center;
  gap: 4px 12px;
  padding: 6px 0;
  border-bottom: 1px solid #f2f4f7;
}
.anna-phase-name {
  font-size: 13px;
  font-weight: 600;
  color: #12233f;
}
.anna-phase-bar {
  height: 8px;
  border-radius: 999px;
  background: #eef1f5;
  overflow: hidden;
  i {
    display: block;
    height: 100%;
    border-radius: 999px;
    background: currentColor;
  }
}
.anna-phase-why {
  font-size: 12px;
  color: #64728c;
  // a line each; the whole reason on hover
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
@media (max-width: 720px) {
  .anna-phase {
    grid-template-columns: 110px 1fr 52px;
  }
  .anna-phase-why {
    grid-column: 1 / -1;
    white-space: normal;
  }
}
.anna-email {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  padding: 8px 14px;
  border: 1px solid #e7ebf1;
  border-radius: 12px;
}
.anna-email-title {
  flex: 1;
  font-size: 13px;
  font-weight: 700;
  color: #12233f;
}
.anna-email-actions {
  display: inline-flex;
  gap: 8px;
}
// the preview opens under the row, the whole width
.anna-email-preview {
  flex-basis: 100%;
}
.anna-email-status {
  font-size: 12px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 999px;
  background: #fff3d6;
  color: #a05a00;
  &--sent {
    background: #e3f3e6;
    color: #2e7d32;
  }
}
.anna-email-subject {
  margin: 6px 0;
  font-size: 13px;
  font-weight: 600;
  color: #3d4757;
}
.anna-email-body {
  white-space: pre-wrap;
  font-size: 13px;
  line-height: 1.55;
  color: #12233f;
  max-height: 200px;
  overflow-y: auto;
  padding: 10px 12px;
  background: #f7f9fc;
  border-radius: 8px;
}
.anna-flag {
  flex: 0 0 10px;
  height: 10px;
  margin-top: 4px;
  border-radius: 50%;
  &--green {
    background: #2e8b45;
  }
  // "not sure": orange
  &--yellow {
    background: #f08c00;
  }
  &--red {
    background: #c0392b;
  }
  &--grey {
    background: #b7c0cc;
  }
}
</style>
